import {
  GetListingSubscriberMembers,
  GetListingSubscriberMembersVariables
} from './__generated__/GetListingSubscriberMembers'

import { gql } from 'apollo-boost'
import { useApolloClient } from '@apollo/react-hooks'

const QUERY = gql`
  query GetListingSubscriberMembers($page: Int, $perPage: Int, $id: String!) {
    marketplaceListing(id: $id) {
      id
      adminInstallations(page: $page, perPage: $perPage) {
        id
        subscriptions {
          id
          muted
          subscriptionSet {
            id
            boards {
              id
              tenant {
                id
                ... on PersonTenant {
                  members {
                    person {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

// Handle the extremely nested response and extract person ids
const extractPersonIds = (input: GetListingSubscriberMembers, includeMuted: boolean): string[] => {
  const resultWithPossibleDuplicates =
    input?.marketplaceListing?.adminInstallations?.flatMap(
      installation =>
        installation.subscriptions
          ?.filter(subscription => includeMuted || !subscription.muted)
          ?.flatMap(
            subscription =>
              subscription.subscriptionSet?.boards?.flatMap(board =>
                board.tenant.__typename === 'PersonTenant'
                  ? board.tenant?.members?.map(member => member.person.id) ?? []
                  : []
              ) ?? []
          ) ?? []
    ) ?? []

  const uniquePersonIds = Array.from(new Set(resultWithPossibleDuplicates))
  return uniquePersonIds
}

const PER_PAGE = 50

export const useGetListingSubscriberMembers = (id: string) => {
  const client = useApolloClient()

  const fetchSubscriberMembers = async (includeMuted: boolean) => {
    try {
      let hasNextPage = true
      let page = 0
      let personIds: string[] = []

      while (hasNextPage) {
        const { data } = await client.query<GetListingSubscriberMembers, GetListingSubscriberMembersVariables>({
          query: QUERY,
          variables: { id, perPage: PER_PAGE, page },
          fetchPolicy: 'no-cache'
        })
        const newPersonIds = extractPersonIds(data, includeMuted)
        personIds = [...personIds, ...newPersonIds]

        hasNextPage = data?.marketplaceListing?.adminInstallations?.length === PER_PAGE
        page++
      }

      return Array.from(new Set(personIds))
    } catch (error) {
      console.error('Error fetching subscriber members:', error)
      return []
    }
  }

  return { fetchSubscriberMembers }
}
